import { useAppConfigStore } from "@/store/appConfig"
import type { RecordingPiece } from "./recordingPieces"

/**
 * What the browser keeps of the last recording, so it can still be cut and used after the page was reloaded.
 * Once the server put the cut video together, only the blocks of the cut are left, see cutBlockIndexes in screenRecordingStore.ts.
 */
export type SavedRecording = { id: string; recordedAt: number; pieces: RecordingPiece[]; audioPeaks: number[]; cutBlockIndexes: number[] | null }

// Azure deletes uploaded pieces that were never put together into a video after a week
const RECOVERABLE_MILLISECONDS = 6 * 24 * 60 * 60 * 1000

let releaseLock = () => {}

// The pieces are stored for one app, so a recording can only be recovered in the app it was recorded in
function savedRecordingKey() {
  return `screenRecording.savedRecording.${useAppConfigStore().appId}`
}

export function loadSavedRecording(): SavedRecording | null {
  const savedRecording: SavedRecording | null = JSON.parse(localStorage.getItem(savedRecordingKey()) ?? "null")
  if (!savedRecording || Date.now() - savedRecording.recordedAt > RECOVERABLE_MILLISECONDS) {
    return null
  }
  return savedRecording
}

export function saveRecording(savedRecording: SavedRecording) {
  try {
    localStorage.setItem(savedRecordingKey(), JSON.stringify(savedRecording))
  } catch {
    // The storage can be full, also of other features. The recording can still be published, only not recovered after a reload.
  }
}

export function forgetSavedRecording() {
  localStorage.removeItem(savedRecordingKey())
}

export function savedRecordingDuration(savedRecording: SavedRecording) {
  return savedRecording.pieces.at(-1)!.end / 1000
}

/**
 * Keeps the other tabs of the admin from recovering the recording while this tab still records or cuts it, because
 * recovering puts it together from the pieces uploaded so far, which drops the ones uploaded later.
 * Resolves to false when another tab has the recording.
 */
export function lockRecording(id: string) {
  return new Promise<boolean>((resolve) => {
    void navigator.locks.request(`screenRecording ${id}`, { ifAvailable: true }, (lock) => {
      resolve(!!lock)
      return lock && new Promise<void>((release) => (releaseLock = release))
    })
  })
}

export function releaseRecordingLock() {
  releaseLock()
}
